import React, { useState, useCallback, useRef, useEffect } from 'react';
import GenericModal from './GenericModal';
import { Plus } from 'lucide-react';
import { EsporaiInput, EsporaiTextarea } from 'components';
import { validateUrlRaw } from 'utils/validUrl.js';
import { brandsApi } from 'api';
import { toast } from 'react-hot-toast';

const CREATOR_STEPS = {
  URL: 1,
  BUSINESS: 2
};

const loadingMessages = [
  'Estamos ingresando al sitio...',
  'Identificando industria...',
  'Analizando competidores...',
  'Encontrando mejores estrategias...',
  'Generando reporte...'
];

const CreateBrandModal = ({ isOpen, onClose, onCreateBrand }) => {
  const [step, setStep] = useState(CREATOR_STEPS.URL);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [brandName, setBrandName] = useState('');
  const [businessDetails, setBusinessDetails] = useState('');
  const [isScraping, setIsScraping] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [invalidURLError, setInvalidURLError] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);

  const hasStartedTypingRef = useRef(false);

  const urlValidation = validateUrlRaw(websiteUrl);
  const isUrlValid = urlValidation.valid;
  const urlError = urlValidation.error;

  const resetForm = useCallback(() => {
    setStep(CREATOR_STEPS.URL);
    setWebsiteUrl('');
    setBrandName('');
    setBusinessDetails('');
    setIsScraping(false);
    setIsCreating(false);
    setLoadingStep(0);
    setInvalidURLError(false);
    hasStartedTypingRef.current = false;
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (isScraping && loadingStep < loadingMessages.length - 1) {
      const timer = setTimeout(() => setLoadingStep((s) => s + 1), 2000);
      return () => clearTimeout(timer);
    }
  }, [isScraping, loadingStep]);

  const handleScrapeAndContinue = async () => {
    if (!isUrlValid) return;

    setIsScraping(true);
    setInvalidURLError(false);
    try {
      const { data } = await brandsApi.scrapeWebsite(websiteUrl);
      setBrandName(data.business_name || '');
      setBusinessDetails(data.business_details || '');
      setStep(CREATOR_STEPS.BUSINESS);
    } catch (err) {
      const isInvalidUrl = err?.response?.data?.error_code === 'INVALID_URL';
      setInvalidURLError(isInvalidUrl);
      toast.error(err?.response?.data?.error || err?.message || 'Error al analizar la URL');
    } finally {
      setIsScraping(false);
      setLoadingStep(0);
    }
  };

  const handleCreate = async () => {
    if (!brandName.trim()) {
      toast.error('El nombre de la marca es requerido');
      return;
    }

    setIsCreating(true);
    try {
      const brandData = {
        name: brandName.trim(),
        website_url: websiteUrl.trim() || undefined,
        business_name: brandName.trim() || undefined,
        business_details: businessDetails.trim() || undefined
      };
      await onCreateBrand(brandData);
      resetForm();
      onClose();
    } catch (error) {
      // El error ya se maneja en el componente padre
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isScraping && !isCreating) {
      resetForm();
      onClose();
    }
  };

  const handleUrlChange = (val) => {
    setWebsiteUrl(val);
    if (val) hasStartedTypingRef.current = true;
  };

  const displayUrlError = inputFocused && hasStartedTypingRef.current ? urlError : null;

  const renderStepContent = () => {
    if (step === CREATOR_STEPS.URL) {
      return (
        <div className="space-y-4">
          <div>
            <label htmlFor="websiteUrl" className="block text-sm font-semibold text-white/80 mb-2">
              URL del sitio web de la marca
            </label>
            <EsporaiInput
              id="websiteUrl"
              type="text"
              value={websiteUrl}
              onChange={handleUrlChange}
              placeholder="www.minegocio.com"
              error={invalidURLError ? 'Verifica que la URL sea correcta' : displayUrlError}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              disabled={isScraping}
              autoFocus
              className={`!bg-slate-900 !text-white placeholder-white/50 caret-white border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark] ${
                invalidURLError || displayUrlError ? '!border-red-500' : '!border-white/30'
              }`}
              style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
            />
            <p className="text-xs text-white/60 mt-1.5">
              Analizaremos el sitio para obtener el nombre y detalles del negocio automáticamente
            </p>
          </div>

          {isScraping && (
            <div className="mt-4 space-y-3">
              {loadingMessages.map((message, index) => (
                <div
                  key={index}
                  className={`flex items-center space-x-2 transition-all duration-500 ${
                    index <= loadingStep ? 'opacity-100' : 'opacity-50'
                  }`}
                >
                  <span className="text-sm text-white/70">{message}</span>
                  {index < loadingStep && <span className="text-green-400">✓</span>}
                  {index === loadingStep && (
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div>
          <label htmlFor="brandName" className="block text-sm font-semibold text-white/80 mb-2">
            Nombre de la Marca
          </label>
          <EsporaiInput
            id="brandName"
            type="text"
            value={brandName}
            onChange={setBrandName}
            placeholder="Nombre de tu negocio"
            disabled={isCreating}
            maxLength={100}
            className="!bg-slate-900 !text-white placeholder-white/50 caret-white !border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
        <div>
          <label htmlFor="businessDetails" className="block text-sm font-semibold text-white/80 mb-2">
            Detalles de la marca
          </label>
          <EsporaiTextarea
            id="businessDetails"
            value={businessDetails}
            onChange={setBusinessDetails}
            placeholder="Detalles de tu negocio"
            disabled={isCreating}
            rows={4}
            className="!bg-slate-900 !text-white placeholder-white/50 caret-white !border-white/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
            style={{ backgroundColor: '#0f172a', color: '#ffffff' }}
          />
        </div>
      </div>
    );
  };

  const isStep1 = step === CREATOR_STEPS.URL;
  const submitText = isStep1
    ? isScraping
      ? 'Analizando...'
      : 'Continuar'
    : isCreating
      ? 'Creando...'
      : 'Crear Marca';
  const submitDisabled = isStep1 ? !isUrlValid || isScraping : !brandName.trim() || isCreating;
  const submitAction = isStep1 ? handleScrapeAndContinue : handleCreate;

  return (
    <GenericModal
      isOpen={isOpen}
      toggle={handleClose}
      titleSlot={
        <div className="flex items-center gap-3 w-full">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/20 border border-blue-400/30 backdrop-blur-md shadow-lg">
            <Plus className="w-5 h-5 text-blue-200" />
          </div>
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold tracking-tight bg-gradient-to-r from-fuchsia-400 to-blue-500 bg-clip-text text-transparent">
              Crear Nueva Marca
            </h2>
            <span className="text-sm text-white/60 mt-0.5">
              {isStep1 ? 'Paso 1: Ingresa la URL del sitio' : 'Paso 2: Revisa y completa los datos'}
            </span>
            <span className="mt-2 w-16 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" />
          </div>
        </div>
      }
      description={
        isStep1
          ? 'Ingresa la URL del sitio web de la marca para analizarla automáticamente'
          : 'Revisa los datos obtenidos y edítalos si es necesario'
      }
      showLogo={false}
      closable={!isScraping && !isCreating}
      showSubmitButton={true}
      showCloseButton={true}
      submitButtonText={submitText}
      closeButtonText={isStep1 ? 'Cancelar' : 'Atrás'}
      onSubmit={submitAction}
      onClose={isStep1 ? handleClose : () => setStep(CREATOR_STEPS.URL)}
      isSubmitting={isScraping || isCreating}
      submitDisabled={submitDisabled}
      width="480px"
      contentPadding="0 2rem 1.5rem 2rem"
    >
      {renderStepContent()}
    </GenericModal>
  );
};

export default CreateBrandModal;
